import { env } from './env';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type ApiRequestOptions = {
  method?: HttpMethod;
  headers?: Record<string, string>;
  body?: unknown;
  authToken?: string | null;
};

type ApiErrorPayload = {
  error?: string;
  message?: string;
  code?: string | number;
};

export class ApiError extends Error {
  public readonly status: number;
  public readonly payload?: ApiErrorPayload;

  public constructor(status: number, message: string, payload?: ApiErrorPayload) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

export const apiClient = {
  async request<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    const { method = 'GET', headers = {}, body, authToken } = options;

    const url = `${env.apiBaseUrl}${path}`;
    if (import.meta.env.DEV && path.includes('auth/login')) {
      console.info('[api-client] Login request URL:', url ? url : `(same-origin)${path}`);
    }
    
    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          ...headers,
        },
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'include',
      });
    } catch (error: unknown) {
      console.error('[api-client] Fetch error:', {
        url,
        method,
        error: error instanceof Error ? error.message : String(error),
        errorName: error instanceof Error ? error.name : 'unknown',
        stack: error instanceof Error ? error.stack : undefined,
      });
      // Wrap network errors in ApiError for consistent error handling
      if (error instanceof Error && error.message === 'Failed to fetch') {
        throw new ApiError(0, 'Unable to connect to server. Please ensure the backend server is running.', {
          error: 'NETWORK_ERROR',
          message: 'Failed to fetch - backend server may be down or unreachable',
        });
      }
      throw error;
    }

    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');
    const isLogin = path.includes('auth/login');
    let payload: unknown;
    if (isJson) {
      const text = await response.text();
      if (!text || text.trim() === '') {
        if (isLogin && response.ok) {
          console.error('[api-client] Login 200 but body empty.', {
            status: response.status,
            contentType,
            bodyLength: text?.length ?? 0,
          });
        }
        if (response.ok) return undefined as T;
        throw new ApiError(response.status, response.statusText);
      }
      try {
        payload = JSON.parse(text) as unknown;
      } catch {
        if (isLogin && response.ok) {
          console.error('[api-client] Login 200 but body is not valid JSON.', {
            status: response.status,
            contentType,
            bodyPreview: text.slice(0, 200),
          });
        }
        if (response.ok) return undefined as T;
        throw new ApiError(response.status, `Non-JSON error response from server (${response.status})`);
      }
      // If server double-encoded (payload is a string), parse once more
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload) as unknown;
        } catch {
          // leave as string
        }
      }
    } else {
      const text = await response.text();
      if (!response.ok) {
        throw new ApiError(response.status, response.statusText, undefined);
      }
      if (!text || text.trim() === '') {
        if (isLogin) {
          console.error('[api-client] Login 200 but body empty (non-JSON response).', {
            status: response.status,
            contentType,
          });
        }
        if (response.ok) return undefined as T;
        throw new ApiError(response.status, response.statusText);
      }
      try {
        payload = JSON.parse(text) as unknown;
      } catch {
        if (isLogin) {
          console.error('[api-client] Login 200 but body is not valid JSON.', {
            status: response.status,
            contentType,
            bodyPreview: text.slice(0, 200),
          });
        }
        if (response.ok) return undefined as T;
        throw new ApiError(response.status, `Non-JSON error response from server (${response.status})`);
      }
    }

    if (!response.ok) {
      const message = (payload as ApiErrorPayload | undefined)?.message ?? response.statusText;
      throw new ApiError(response.status, message, payload as ApiErrorPayload);
    }

    if (import.meta.env.DEV && path.includes('auth/login') && response.ok) {
      const hasToken =
        payload != null &&
        typeof payload === 'object' &&
        ('sessionToken' in payload || 'token' in payload);
      if (!hasToken) {
        console.warn('[api-client] Login 200 but no token in payload:', {
          status: response.status,
          contentType,
          payload,
        });
      }
    }

    return payload as T;
  },

  get<T>(path: string, authToken?: string | null) {
    return this.request<T>(path, { method: 'GET', authToken });
  },

  post<T, B = unknown>(path: string, body: B, authToken?: string | null) {
    return this.request<T>(path, { method: 'POST', body, authToken });
  },

  patch<T, B = unknown>(path: string, body: B, authToken?: string | null) {
    return this.request<T>(path, { method: 'PATCH', body, authToken });
  },

  put<T, B = unknown>(path: string, body: B, authToken?: string | null) {
    return this.request<T>(path, { method: 'PUT', body, authToken });
  },

  delete<T>(path: string, authToken?: string | null) {
    return this.request<T>(path, { method: 'DELETE', authToken });
  },
};
